import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import {
  EventOverviewStatusByEventValue,
  getUserStat,
  UserStatView,
} from './useUserStat'

export type EventOverviewSortBy = 'name' | 'role' | 'class' | 'custom'
export type EventOverviewSortDirection = 'asc' | 'desc'
export type EventOverviewSimpleSortBy = Exclude<EventOverviewSortBy, 'custom'>

export type EventOverviewRow = {
  key: string | number
  main: wowaudit.TeamMembership
  membersForUser: wowaudit.TeamMembership[]
  statusByEvent: Map<number, EventOverviewStatusByEventValue>
}
const roleSortOrder = new Map(
  characterRoles.map((role, index) => [role, index]),
)

type SortableMemberFields = {
  name: string
  role: string
  characterClass: string
}

type SortEventOverviewRowsInput = {
  rows: EventOverviewRow[]
  visibleEvents: wowaudit.EventReference[]
  statView: UserStatView
  sortBy: EventOverviewSortBy
  sortDirection: EventOverviewSortDirection
}

export const compareSortableMemberFields = ({
  left,
  right,
  sortBy,
}: {
  left: SortableMemberFields
  right: SortableMemberFields
  sortBy: EventOverviewSimpleSortBy
}) => {
  const compareValue = (() => {
    if (sortBy === 'role') {
      const leftRoleIndex =
        roleSortOrder.get(left.role as typeof characterRoles[number]) ??
        characterRoles.length
      const rightRoleIndex =
        roleSortOrder.get(right.role as typeof characterRoles[number]) ??
        characterRoles.length
      if (
        leftRoleIndex !== characterRoles.length &&
        rightRoleIndex !== characterRoles.length
      ) {
        return leftRoleIndex - rightRoleIndex
      }
      if (leftRoleIndex !== characterRoles.length) return -1
      if (rightRoleIndex !== characterRoles.length) return 1
      return left.role.localeCompare(right.role)
    }

    if (sortBy === 'class') {
      return left.characterClass.localeCompare(right.characterClass)
    }

    return left.name.localeCompare(right.name)
  })()

  if (compareValue !== 0) return compareValue
  return left.name.localeCompare(right.name)
}

export const sortEventOverviewRows = ({
  rows,
  visibleEvents,
  statView,
  sortBy,
  sortDirection,
}: SortEventOverviewRowsInput): EventOverviewRow[] => {
  const sortedRows = [...rows].sort((leftRow, rightRow) => {
    const leftMain = leftRow.main
    const rightMain = rightRow.main

    const compareValue = (() => {
      if (sortBy === 'custom') {
        const leftSortValue = getUserStat({
          visibleEvents,
          statusByEvent: leftRow.statusByEvent,
          statView,
        }).sortValue
        const rightSortValue = getUserStat({
          visibleEvents,
          statusByEvent: rightRow.statusByEvent,
          statView,
        }).sortValue
        return leftSortValue - rightSortValue
      }

      return compareSortableMemberFields({
        left: {
          name: leftMain.characterReference.name,
          role: leftMain.role,
          characterClass: leftMain.characterReference.characterClass,
        },
        right: {
          name: rightMain.characterReference.name,
          role: rightMain.role,
          characterClass: rightMain.characterReference.characterClass,
        },
        sortBy,
      })
    })()

    if (compareValue !== 0) {
      return sortDirection === 'asc' ? compareValue : -compareValue
    }

    return leftMain.characterReference.name.localeCompare(
      rightMain.characterReference.name,
    )
  })

  return sortedRows
}
